/**
 * ABOUTME: Chat card component for displaying period-over-period comparison data.
 * Rendered when the analytics agent returns compare_periods results via message.metadata.period_comparison.
 */
import { ArrowUp, ArrowDown, Minus, BarChart3 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { CHAT_CARD } from '@/components/chat/chatCard'
import { formatDateRange } from '@/components/chat/chatCardDate'

interface PeriodData {
  start_date: string
  end_date: string
  total: number
  average: number
}

interface ComparisonData {
  absolute_change: number
  // null when no percentage is computable — see change_state for why
  percent_change: number | null
  direction: string
  change_state?: 'no_prior_data' | 'first_activity' | 'normal'
}

interface PeriodComparisonData {
  metric_type: string
  period1: PeriodData
  period2: PeriodData
  comparison: ComparisonData
}

interface PeriodComparisonCardProps {
  data: PeriodComparisonData
}

const CURRENCY_METRICS = new Set(['revenue', 'ad_spend'])

function formatValue(value: number, metricType: string): string {
  if (CURRENCY_METRICS.has(metricType)) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  }
  if (metricType === 'roas') {
    return `${value.toFixed(2)}x`
  }
  return new Intl.NumberFormat('en-US').format(value)
}

function formatSignedValue(value: number, metricType: string): string {
  const sign = value >= 0 ? '+' : '-'
  return `${sign}${formatValue(Math.abs(value), metricType)}`
}

function metricNoun(metricType: string): string {
  // Plain-English noun used in the card title.
  const labels: Record<string, string> = {
    engagement: 'engagement',
    reach: 'reach',
    impressions: 'impressions',
    followers: 'followers',
    clicks: 'clicks',
    revenue: 'revenue',
    leads: 'leads',
    conversions: 'conversions',
    ad_spend: 'ad spend',
    roas: 'ROAS',
    content: 'content performance',
  }
  return labels[metricType] || metricType.replace(/_/g, ' ')
}

function metricLabel(metricType: string): string {
  const noun = metricNoun(metricType)
  return noun.charAt(0).toUpperCase() + noun.slice(1)
}

function DirectionArrow({ direction }: { direction: string }) {
  if (direction === 'improved' || direction === 'new_activity')
    return <ArrowUp className="text-success size-3.5" />
  if (direction === 'declined') return <ArrowDown className="text-destructive size-3.5" />
  return <Minus className="text-muted-foreground size-3.5" />
}

export function PeriodComparisonCard({ data }: PeriodComparisonCardProps) {
  const { comparison } = data
  const isPositive = comparison.direction === 'improved' || comparison.direction === 'new_activity'
  const isNegative = comparison.direction === 'declined'

  const absChange = formatSignedValue(comparison.absolute_change, data.metric_type)
  // percent_change is null exactly when there's no prior value to take a
  // percentage of (change_state "no_prior_data" or "first_activity") — a
  // literal 0% would instead mean two genuinely equal nonzero periods.
  const pctChange =
    comparison.percent_change === null
      ? comparison.change_state === 'first_activity'
        ? 'new'
        : 'n/a'
      : comparison.percent_change >= 0
        ? `+${comparison.percent_change}%`
        : `${comparison.percent_change}%`

  return (
    <div className={cn(CHAT_CARD, 'my-2 space-y-2')} data-testid="period-comparison-card">
      <h3 className="flex items-center gap-1.5 text-sm font-medium">
        <BarChart3 className="text-muted-foreground size-4" />
        {metricLabel(data.metric_type)} comparison
      </h3>
      <div>
        {/* Headline data — direction color on the text, not a tinted slab */}
        <div
          className={cn(
            'mb-2 flex items-center gap-1.5 text-sm font-medium',
            isPositive && 'text-success',
            isNegative && 'text-destructive',
            !isPositive && !isNegative && 'text-muted-foreground'
          )}
        >
          <DirectionArrow direction={comparison.direction} />
          <span>
            {absChange} ({pctChange})
          </span>
        </div>

        {/* Period totals (compact, no axis chart) */}
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="rounded-md border px-2 py-1.5">
            <div className="text-muted-foreground">
              {formatDateRange(data.period1.start_date, data.period1.end_date)}
            </div>
            <div className="text-sm font-semibold">
              {formatValue(data.period1.total, data.metric_type)}
            </div>
          </div>
          <div className="rounded-md border px-2 py-1.5">
            <div className="text-muted-foreground">
              {formatDateRange(data.period2.start_date, data.period2.end_date)}
            </div>
            <div className="text-sm font-semibold">
              {formatValue(data.period2.total, data.metric_type)}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
